import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducer';
import {AuthorReport, SearchProps} from 'app/interfaces';
import { FacetSearch, Search } from 'app/models';
import { MyCoverageService } from 'app/services';
import { ExportFilenameService } from 'app/components';
import { eq, find } from 'lodash';
import { Subscription } from 'rxjs';
import { Table } from 'primeng/table';

import * as actions from 'redux/actions';
import { Paginator } from 'primeng/paginator';
import {ReportTableActions} from '../report-table-actions';



@Component({
  selector: 'app-authors-tab',
  templateUrl: './authors-tab.component.html',
  styleUrls: ['../my-coverage-reporting.component.scss'],
})
export class AuthorsTabComponent extends ReportTableActions implements OnInit, OnChanges, OnDestroy {

  public authorsData: AuthorReport[];
  public loading: boolean;
  public pageSize: number;
  public page: number;
  public totalItems: number;
  public totalPages: number;
  public currentPage: number;
  public searchBody: Search;
  public sort: string;
  public sortColumn: string;
  public savingAuthorId: number | null = null;
  private savedAuthors: any[] = [];
  private authorsOutletsSub: Subscription;

  @Input() projectId: number;
  @Input() myCompanyQuery: string;
  @Input() contentKeywords: string;
  @Input() coverageReportRequest: SearchProps;
  @Input() reportName: string;
  @ViewChild('paginator', { static: false }) paginator: Paginator;
  @ViewChild('authorsTable', { static: false }) table: Table;
  @ViewChildren(
    'sortNameIcon, sortLastMentionedIcon, sortFirstMentionedIcon, sortArticlesIcon, sortTotalCoverageIcon',
    {
      read: ElementRef,
    }
  )
  sortIcons: QueryList<ElementRef>;

  constructor(
    private myCoverageService: MyCoverageService,
    private store: Store<AppState>,
    exportFilenameService: ExportFilenameService
  ) {
    super(myCoverageService, store, exportFilenameService);
    this.authorsData = [];
    this.loading = false;
    this.pageSize = 25;
    this.page = 1;
    this.totalItems = 0;
    this.totalPages = 0;
    this.currentPage = 1;
  }

  ngOnInit() {
    this.getAuthorsData();

    this.store.select('search').subscribe(({ searchBody }) => {
      if (searchBody) {
        this.searchBody = searchBody;
      }
    });

    this.authorsOutletsSub = this.store
      .select('authorsOutletsList')
      .subscribe(({ authors, isAddingAuthorOutlet, isDeletingAuthorOutlet }) => {
        this.savedAuthors = authors || [];
        if (!isAddingAuthorOutlet && !isDeletingAuthorOutlet) {
          this.savingAuthorId = null;
        }
      });
  }

  ngOnDestroy() {
    if (this.authorsOutletsSub) { this.authorsOutletsSub.unsubscribe(); }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.coverageReportRequest && !changes.coverageReportRequest.firstChange) {
      this.page = 1;
      this.getAuthorsData();
    }
  }



  getAuthorsData() {
    if (!this.projectId) {
      return;
    }
    this.loading = true;
    this.myCoverageService
      .getAuthorsReport(this.projectId, this.page - 1, this.pageSize, this.sort, this.coverageReportRequest || { timeframe: 'ONE_YEAR' })
      .subscribe(
        (response: {
          content: AuthorReport[];
          totalElements: number;
          totalPages: number;
          size: number;
          number: number;
          numberOfElements: number;
        }) => {
          this.authorsData = response.content || [];
          this.totalItems = response.totalElements || 0;
          this.totalPages = response.totalPages || 0;
          this.pageSize = response.size || 50;
          this.currentPage = response.number || 0;
          this.loading = false;
        },
        (error) => {
          console.error('Error fetching authors data:', error);
          this.authorsData = [];
          this.totalItems = 0;
          this.totalPages = 0;
          this.loading = false;
        }
      );
  }

  getAuthorContent(authorItem) {
    const reportRequest: any = this.coverageReportRequest || {};
    const reportFrom = reportRequest.periodFrom || reportRequest.from;
    const reportTo = reportRequest.periodTo || reportRequest.to;
    const facetSearch = new FacetSearch({
      searchBody: new Search({
        ...this.searchBody,
        ...reportRequest,
        keywords: this.contentKeywords || reportRequest.keywords || this.myCompanyQuery,
        timeframe: reportFrom ? 'CUSTOM' : reportRequest.timeframe,
        from: reportFrom ? new Date(reportFrom) : null,
        to: reportTo ? new Date(reportTo) : null,
        authors: [authorItem.authorId],
      }),
    });

    this.store.dispatch(
      actions.loadAuthorContent({
        facetSearch,
        author: {
          ...authorItem,
          id: authorItem.authorId,
          name: authorItem.name,
          outletNames: authorItem.outletNames,
        },
      })
    );
  }

  protected getReportExportName(): string {
    return this.reportName;
  }

  getPage(e: any) {
    this.currentPage = e.page;
    this.page = e.page + 1;
    this.getAuthorsData();
    if (this.table) {
      setTimeout(() => {
        this.table.scrollTo({ top: 0 });
      }, 300);
    }
  }

  sortChange(icon: any) {
    this.sortIcons.forEach(({ nativeElement }) => {
      if (eq(nativeElement, icon)) {
        if (eq(icon.id, 'sortNameIcon')) {
          icon.className = eq(icon.className, 'pi pi-sort')
            ? 'pi pi-sort-up'
            : eq(icon.className, 'pi pi-sort-up')
            ? 'pi pi-sort-down'
            : 'pi pi-sort-up';
        } else {
          icon.className = eq(icon.className, 'pi pi-sort')
            ? 'pi pi-sort-down'
            : eq(icon.className, 'pi pi-sort-up')
            ? 'pi pi-sort-down'
            : 'pi pi-sort-up';
        }
      } else {
        nativeElement.className = 'pi pi-sort';
      }
    });

    switch (icon.id) {
      case 'sortNameIcon':
        this.sort = eq(icon.className, 'pi pi-sort-up') ? 'name' : 'name,desc';
        this.sortColumn = 'name';
        break;

      case 'sortLastMentionedIcon':
        this.sort = eq(icon.className, 'pi pi-sort-up')
          ? 'maxDate'
          : 'maxDate,desc';
        this.sortColumn = 'maxDate';
        break;

      case 'sortFirstMentionedIcon':
        this.sort = eq(icon.className, 'pi pi-sort-up')
          ? 'minDate'
          : 'minDate,desc';
        this.sortColumn = 'minDate';
        break;

      case 'sortArticlesIcon':
        this.sort = eq(icon.className, 'pi pi-sort-up')
          ? 'numItems'
          : 'numItems,desc';
        this.sortColumn = 'numItems';
        break;

      case 'sortTotalCoverageIcon':
        this.sort = eq(icon.className, 'pi pi-sort-up')
          ? 'totalScore'
          : 'totalScore,desc';
        this.sortColumn = 'totalScore';
        break;

      default:
        this.sortColumn = '';
        break;
    }

    this.page = 1;
    this.getAuthorsData();

    if (this.table) {
      setTimeout(() => {
        this.table.scrollTo({ top: 0 });
      }, 300);
    }
  }

  isAuthorSaved(item: AuthorReport): boolean {
    return !!find(this.savedAuthors, (a) => eq(a.partyId, item.authorId));
  }

  onAuthorStarToggled(item: AuthorReport, currentActive: boolean): void {
    this.savingAuthorId = item.authorId;
    const userProject = { value: this.projectId };
    const author = { id: item.authorId, name: item.name };
    if (currentActive) {
      this.store.dispatch(actions.deleteAuthorToProject({ partyId: item.authorId, userProject, author }));
    } else {
      this.store.dispatch(actions.addAuthorToProject({ partyId: item.authorId, userProject, author }));
    }
  }

  goToTitleUrl(url) { window.open(url, '_blank'); }
}
